import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Videocam as VideoIcon,
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useDropzone } from "react-dropzone";
import api from "../services/api";
import { format } from "date-fns";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Appeals: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [appeals, setAppeals] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const [formData, setFormData] = useState({
    orderId: "",
    reason: "",
    comment: "",
    requireClientInvoice: false,
    requireTraderInvoice: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appealsData, ordersData] = await Promise.all([
        api.getAppeals({ limit: 50 }),
        api.getOrders({ limit: 100 }),
      ]);

      setAppeals(appealsData.data || []);
      setOrders(ordersData.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/pdf": [".pdf"],
      "video/*": [".mp4", ".avi", ".mov"],
    },
  });

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) return <ImageIcon />;
    if (file.type === "application/pdf") return <PdfIcon />;
    if (file.type.startsWith("video/")) return <VideoIcon />;
    return <CloudUploadIcon />;
  };

  const handleCreateAppeal = async () => {
    setLoading(true);
    try {
      let uploadedFileData = {
        images: [] as any[],
        videos: [] as any[],
        documents: [] as any[],
      };

      // Upload files if any
      if (uploadedFiles.length > 0) {
        console.log(`📤 Загружаем ${uploadedFiles.length} файл(ов)...`);
        const uploadResult = await api.uploadAppealFiles(uploadedFiles);

        // Distribute files by type
        uploadResult.files.forEach((file: any) => {
          if (file.type === "image") {
            uploadedFileData.images.push(file);
          } else if (file.type === "video") {
            uploadedFileData.videos.push(file);
          } else if (file.type === "document") {
            uploadedFileData.documents.push(file);
          }
        });

        console.log(`✅ Файлы загружены:`, uploadedFileData);
      }

      const appealData = {
        ...formData,
        images: uploadedFileData.images,
        videos: uploadedFileData.videos,
        documents: uploadedFileData.documents,
      };

      console.log(`📋 Отправляем апелляцию:`, appealData);
      await api.createAppeal(appealData);
      alert("Апелляция создана успешно!");

      // Reset form
      setFormData({
        orderId: "",
        reason: "",
        comment: "",
        requireClientInvoice: false,
        requireTraderInvoice: false,
      });
      setUploadedFiles([]);
      fetchData();
    } catch (error: any) {
      console.error("❌ Ошибка создания апелляции:", error);
      alert(`Ошибка: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "Appeal ID", width: 120 },
    { field: "orderId", headerName: "Order ID", width: 120 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "resolved"
              ? "success"
              : params.value === "pending"
              ? "warning"
              : params.value === "rejected"
              ? "error"
              : "default"
          }
          size="small"
        />
      ),
    },
    { field: "reason", headerName: "Reason", width: 200 },
    {
      field: "requireClientInvoice",
      headerName: "Client Invoice",
      width: 120,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "requireTraderInvoice",
      headerName: "Trader Invoice",
      width: 120,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 180,
      valueFormatter: (params) =>
        format(new Date(params.value), "dd.MM.yyyy HH:mm:ss"),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Appeal Testing
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Create Appeal" />
          <Tab label="Appeal List" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Appeal Details
              </Typography>

              <FormControl fullWidth margin="normal">
                <InputLabel>Order</InputLabel>
                <Select
                  value={formData.orderId}
                  onChange={(e) =>
                    setFormData({ ...formData, orderId: e.target.value })
                  }
                >
                  <MenuItem value="">Select Order</MenuItem>
                  {orders.map((order) => (
                    <MenuItem key={order.id} value={order.id}>
                      {order.invId} - {order.amount} {order.currency} (
                      {order.status})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Reason"
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                margin="normal"
                multiline
                rows={2}
              />

              <TextField
                fullWidth
                label="Comment"
                value={formData.comment}
                onChange={(e) =>
                  setFormData({ ...formData, comment: e.target.value })
                }
                margin="normal"
                multiline
                rows={3}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requireClientInvoice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requireClientInvoice: e.target.checked,
                      })
                    }
                  />
                }
                label="Require Client Invoice"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requireTraderInvoice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requireTraderInvoice: e.target.checked,
                      })
                    }
                  />
                }
                label="Require Trader Invoice"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                File Upload
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed #666",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive
                    ? "action.hover"
                    : "background.paper",
                  mb: 2,
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon
                  sx={{ fontSize: 48, mb: 2, color: "text.secondary" }}
                />
                <Typography>
                  {isDragActive
                    ? "Drop the files here..."
                    : "Drag & drop files here, or click to select"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Supported: Images (PNG, JPG), PDF, Videos (MP4, AVI)
                </Typography>
              </Box>

              {uploadedFiles.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Uploaded Files ({uploadedFiles.length})
                  </Typography>
                  <List>
                    {uploadedFiles.map((file, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => removeFile(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>{getFileIcon(file)}</ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024 / 1024).toFixed(
                            2
                          )} MB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Files will be uploaded when you create the appeal. Maximum
                  file size: 10MB per file.
                </Typography>
              </Alert>

              <Button
                variant="contained"
                fullWidth
                onClick={handleCreateAppeal}
                disabled={loading || !formData.orderId}
                sx={{ mt: 3 }}
              >
                Create Appeal
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <DataGrid
            rows={appeals}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            onRowClick={(params) => setSelectedAppeal(params.row)}
          />
        </TabPanel>
      </Paper>

      <Dialog
        open={!!selectedAppeal}
        onClose={() => setSelectedAppeal(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Appeal Details</DialogTitle>
        <DialogContent>
          {selectedAppeal && (
            <Box>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedAppeal.id}
              </Typography>
              <Typography variant="body2">
                <strong>Order ID:</strong> {selectedAppeal.orderId}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {selectedAppeal.status}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {selectedAppeal.reason}
              </Typography>
              <Typography variant="body2">
                <strong>Comment:</strong> {selectedAppeal.comment}
              </Typography>
              <Typography variant="body2">
                <strong>Client Invoice Required:</strong>{" "}
                {selectedAppeal.requireClientInvoice ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2">
                <strong>Trader Invoice Required:</strong>{" "}
                {selectedAppeal.requireTraderInvoice ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2">
                <strong>TTL:</strong> {selectedAppeal.ttl} seconds
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong>{" "}
                {format(
                  new Date(selectedAppeal.createdAt),
                  "dd.MM.yyyy HH:mm:ss"
                )}
              </Typography>

              {selectedAppeal.order && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Order Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {selectedAppeal.order.amount}{" "}
                    {selectedAppeal.order.currency}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedAppeal.order.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Provider:</strong>{" "}
                    {selectedAppeal.order.provider?.name || "N/A"}
                  </Typography>
                </>
              )}

              {(selectedAppeal.images ||
                selectedAppeal.videos ||
                selectedAppeal.documents) && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Attachments
                  </Typography>
                  {selectedAppeal.images && (
                    <Typography variant="body2">
                      <strong>Images:</strong> {selectedAppeal.images}
                    </Typography>
                  )}
                  {selectedAppeal.videos && (
                    <Typography variant="body2">
                      <strong>Videos:</strong> {selectedAppeal.videos}
                    </Typography>
                  )}
                  {selectedAppeal.documents && (
                    <Typography variant="body2">
                      <strong>Documents:</strong> {selectedAppeal.documents}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Appeals;
